import type { RobotRead } from 'api/schema/models/RobotRead'
import type { TFunction } from 'i18next'
import { ROBOT_COMMANDS, ROBOT_MODES, ROBOT_NETWORK_STATUSES, TASK_RUNTIME_STATE } from 'utils/constants'
import type { IRobotInfo } from 'utils/interfaces'
import { missionProgressFromLiveExecution, readLiveExecutionFromRobotRead } from 'utils/robotLiveExecution'
import { VENDOR } from 'utils/vendorDisplayName'

export function translateTaskRuntimeState(t: TFunction, state: TASK_RUNTIME_STATE): string {
  return t(`common.task_runtime.${state}`)
}

/** Live telemetry mission_progress-shaped object (map, state, name, …). */
export function getLastOdysseyMissionProgress(robot: IRobotInfo | undefined): Record<string, unknown> | undefined {
  if (robot?.vendor === VENDOR.GAUSIUM && robot.robotRaw) {
    const le = readLiveExecutionFromRobotRead(robot.robotRaw as RobotRead)
    if (le) {
      const mp = missionProgressFromLiveExecution(le)
      if (mp && Object.keys(mp).length) return mp
    }
  }
  const odysseys = robot?.robotRaw?.odysseys
  if (!odysseys?.length) return undefined
  const last = odysseys[odysseys.length - 1]
  const msl = last?.mission_status_last as Record<string, unknown> | undefined
  const mp = msl?.mission_progress
  return mp && typeof mp === 'object' ? (mp as Record<string, unknown>) : undefined
}

export function parseGausiumMissionProgressState(raw: string | undefined): TASK_RUNTIME_STATE {
  if (!raw) return TASK_RUNTIME_STATE.UNKNOWN
  switch (raw.toUpperCase()) {
    case 'RUNNING':
      return TASK_RUNTIME_STATE.RUNNING
    case 'PAUSED':
      return TASK_RUNTIME_STATE.PAUSED
    case 'IDLE':
      return TASK_RUNTIME_STATE.IDLE
    case 'UNKNOWN':
      return TASK_RUNTIME_STATE.UNKNOWN
    default:
      return TASK_RUNTIME_STATE.UNKNOWN
  }
}

/**
 * Single source of truth for pause/resume/stop affordances and task status copy.
 */
export function getTaskRuntimeState(robot: IRobotInfo | undefined): TASK_RUNTIME_STATE {
  if (!robot) return TASK_RUNTIME_STATE.UNKNOWN

  if (robot.vendor === VENDOR.GAUSIUM) {
    // Cached mission_progress can still report RUNNING/PAUSED after disconnect; GS is only "live" online.
    if (robot.networkStatus !== ROBOT_NETWORK_STATUSES.ONLINE) {
      return TASK_RUNTIME_STATE.IDLE
    }
    const mp = getLastOdysseyMissionProgress(robot)
    const state = parseGausiumMissionProgressState(typeof mp?.state === 'string' ? mp.state : undefined)
    const taskName = typeof mp?.name === 'string' ? mp.name.trim() : ''
    // GS may transiently report RUNNING while executing task payload is already empty.
    // In this case UI should show the robot as idle, matching vendor dashboard behavior.
    if (state === TASK_RUNTIME_STATE.RUNNING) {
      if (!taskName) {
        return TASK_RUNTIME_STATE.IDLE
      }
    }
    return state
  }

  if (robot.activeMode === ROBOT_MODES.MODE_AUTOMATIC) {
    if (robot.activeCommand === ROBOT_COMMANDS.PAUSE) return TASK_RUNTIME_STATE.PAUSED
    if (robot.activeCommand === ROBOT_COMMANDS.START) return TASK_RUNTIME_STATE.RUNNING
    return TASK_RUNTIME_STATE.UNKNOWN
  }

  if (robot.activeMode === ROBOT_MODES.MODE_NOT_SET) return TASK_RUNTIME_STATE.IDLE

  return TASK_RUNTIME_STATE.UNKNOWN
}

export function hasActiveTaskRuntimeState(robot: IRobotInfo | undefined): boolean {
  const state = getTaskRuntimeState(robot)
  return state === TASK_RUNTIME_STATE.RUNNING || state === TASK_RUNTIME_STATE.PAUSED
}

/**
 * Whether Mission Control should stay open.
 * Same signal as the Pause/Continue button, including an offline robot whose last
 * snapshot still says the mission is running or paused.
 */
export function shouldKeepMissionControl(robot: IRobotInfo | undefined): boolean {
  if (!robot) return false
  if (robot.vendor === VENDOR.GAUSIUM) {
    const mp = getLastOdysseyMissionProgress(robot)
    const state = parseGausiumMissionProgressState(typeof mp?.state === 'string' ? mp.state : undefined)
    const taskName = typeof mp?.name === 'string' ? mp.name.trim() : ''
    if (state === TASK_RUNTIME_STATE.RUNNING && !taskName) return false
    return state === TASK_RUNTIME_STATE.RUNNING || state === TASK_RUNTIME_STATE.PAUSED
  }
  return robot.activeMode === ROBOT_MODES.MODE_AUTOMATIC
}
